import { useMemo, useState } from "react";
import { Star, ExternalLink } from "lucide-react";
import { useBrokers } from "@/lib/format";
import { PageHead } from "@/components/PageHead";
import { Glossary } from "@/components/Glossary";
import { BrokerTable } from "@/components/home/BrokerSnapshot";
import { useLead } from "@/components/layout/Layout";
import { useFocusParam } from "@/lib/useFocus";
import { goUrl } from "@/lib/data";
import { CompareToggle, CompareTray, useCompareSelect } from "@/components/compare/CompareTray";

const SORTS = [["active_clients_lakh", "Most clients"], ["rating", "Top rated"], ["name", "A–Z"]];

export default function Brokers() {
  const { data } = useBrokers();
  const openLead = useLead();
  const [type, setType] = useState("All");
  const [sort, setSort] = useState("active_clients_lakh");
  const sel = useCompareSelect();
  useFocusParam(!!data?.items?.length);
  const items = useMemo(() => {
    const list = (data?.items || []).filter((b) => type === "All" || b.type === type);
    return [...list].sort((a, b) => (sort === "name" ? a.name.localeCompare(b.name) : b[sort] - a[sort]));
  }, [data, type, sort]);

  return (
    <div data-testid="brokers-page">
      <PageHead eyebrow="Stock Brokers" title="Find the right Demat & trading account." sub="Compare brokerage, annual charges, platforms and client base across India's leading SEBI-registered brokers." />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap gap-3 justify-between mb-5">
          <div className="inline-flex rounded-xl bg-slate-200/70 p-1">
            {["All", "Discount", "Full-service"].map((t) => <button key={t} data-testid={`broker-type-${t.toLowerCase()}`} onClick={() => setType(t)} className={`h-9 px-4 rounded-lg text-sm font-bold ${type === t ? "bg-white shadow" : "text-slate-500"}`}>{t}</button>)}
          </div>
          <div className="flex gap-2">
            {SORTS.map(([k, l]) => <button key={k} data-testid={`broker-sort-${k}`} onClick={() => setSort(k)} className={`h-9 px-3 rounded-lg text-sm font-semibold border ${sort === k ? "bg-[#1E293B] text-white border-[#1E293B]" : "border-slate-300 text-slate-600"}`}>{l}</button>)}
          </div>
        </div>
        <BrokerTable items={items} />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {items.map((b) => (
            <div key={b.id} data-focus={b.id} data-testid={`broker-card-${b.id}`} className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between gap-2">
                <div><p className="font-display font-bold text-xl">{b.name}</p><p className="text-xs text-slate-500">{b.platforms}</p></div>
                <span className="inline-flex items-center gap-1 text-sm font-bold"><Star className="w-4 h-4 fill-amber-400 text-amber-400" />{b.rating}</span>
              </div>
              <span className="self-start mt-3 rounded-full bg-amber-50 border border-amber-200 text-amber-800 px-2.5 py-0.5 text-[11px] font-bold">{b.highlight}</span>
              <p className="mt-4 text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#059669]">Best suited for</p>
              <p className="text-sm text-slate-700 mt-1 flex-1">{b.best_for}</p>
              <div className="mt-5 flex gap-2">
                <button onClick={() => openLead({ id: b.id, company_name: b.name })} data-testid={`broker-card-open-${b.id}`} className="flex-1 h-11 rounded-xl bg-[#10B981] hover:bg-[#059669] text-[#0F172A] font-bold">Open Account</button>
                <a href={goUrl(b.id, "card")} target="_blank" rel="noopener noreferrer" data-testid={`broker-card-site-${b.id}`} aria-label={`Visit ${b.name} website`} className="h-11 px-3 rounded-xl border border-slate-300 hover:border-slate-500 inline-flex items-center gap-1.5 text-sm font-bold">Visit<ExternalLink className="w-4 h-4" /></a>
                <CompareToggle id={b.id} sel={sel} />
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-6">{data?.note}</p>
        <Glossary set="brokers" />
        <CompareTray sel={sel} type="brokers" names={Object.fromEntries((data?.items || []).map((b) => [b.id, b.name]))} />
      </div>
    </div>
  );
}
